import { createStore } from 'vuex'
import ui from '@/store/ui'
import ai from '@/store/ai'
import user from '@/store/user'
import project from '@/store/project'
import cmd from '@/store/cmd'
import io from '@/store/io'

const { VUE_APP_AI_BACKEND } = process.env

console.log('VUE_APP_AI_BACKEND', VUE_APP_AI_BACKEND)
// Debug plugin to trace play mutations (for crossover freeze investigation)
const debugPlayPlugin = (store) => {
  store.subscribe((mutation, state) => {
    if (mutation.type === 'play') {
      const err = new Error()
      const stack = (err.stack || '').split('\n').slice(1, 8).join(' | ')
      // eslint-disable-next-line no-console
      console.info('[Store] play via subscribe', {
        next: mutation.payload,
        currentTime: state?.project?.timeline?.currentTime,
        stack,
        ts: (typeof performance !== 'undefined' && performance.now)
          ? performance.now() / 1000
          : Date.now() / 1000,
      })
    }
  })
}

// Dev/QA helpers exposed under window.__vyby.* so engineers can inspect
// state from the browser console without scrubbing through Vue devtools.
const devGlobalsPlugin = (store) => {
  if (typeof window === 'undefined') return
  window.__vyby = window.__vyby || {}
  // Print and return the export In/Out range. Useful for verifying I/O
  // hotkeys without opening Vuex devtools.
  window.__vyby.inOut = () => {
    const tl = store.state.project?.timeline || {}
    const info = {
      in: tl.exportIn,
      out: tl.exportOut,
      auto: tl.exportAuto,
      duration: Number.isFinite(tl.exportOut) && Number.isFinite(tl.exportIn)
        ? Math.max(0, tl.exportOut - tl.exportIn)
        : null,
      currentTime: tl.currentTime,
      length: tl.length,
    }
    // eslint-disable-next-line no-console
    console.info('[__vyby.inOut]', info)
    return info
  }
  window.__vyby.setIn = (t) => {
    store.commit('project/setExportIn', Number(t))
    return window.__vyby.inOut()
  }
  window.__vyby.setOut = (t) => {
    store.commit('project/setExportOut', Number(t))
    return window.__vyby.inOut()
  }
  window.__vyby.clearInOut = () => {
    store.commit('project/setExportAuto', true)
    return window.__vyby.inOut()
  }

  // Phase 8a: introspect which encoder backend is available for export.
  // Returns the probe result + which backend would be picked. Lazy-loads
  // the probe module so it doesn't pull into the main bundle for users
  // who never touch export settings.
  window.__vyby.exportEncoderInfo = async (opts = {}) => {
    try {
      const mod = await import('@/editor/render/encoderSupport')
      // Use the project settings as the probe target so we test the
      // codec config the user will actually export with, not a generic
      // 1080p baseline.
      const settings = store.state.project?.settings || {}
      const info = await mod.pickExportEncoder({
        width: Number(settings.width) || 1920,
        height: Number(settings.height) || 1080,
        fps: Number(settings.fps) || 24,
        sampleRate: 48000,
        ...opts,
      })
      // eslint-disable-next-line no-console
      console.info('[__vyby.exportEncoderInfo]', info)
      return info
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[__vyby.exportEncoderInfo] failed', e)
      return null
    }
  }

  // Phase 9: snapshot of the export-form selections + whether a save
  // location handle is currently armed on the editor instance. The
  // handle itself isn't returned (it's a non-serializable object).
  window.__vyby.exportSettings = () => {
    try {
      const tl = store.state.project?.timeline || {}
      const editor = (typeof window !== 'undefined') ? window.__vybyEditor : null
      const info = {
        filename: tl.exportFilename ?? null,
        codec: tl.exportCodec ?? null,
        hasSaveHandle: !!(editor && editor._pendingExportFileHandle),
        // Also surface the in-flight render's handle when an export is
        // currently running so QA can confirm the streaming-to-disk path.
        hasActiveRenderHandle: !!(editor && editor._render && editor._render._exportFileHandle),
      }
      // eslint-disable-next-line no-console
      console.info('[__vyby.exportSettings]', info)
      return info
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[__vyby.exportSettings] failed', e)
      return null
    }
  }

  // Phase 7: snapshot of decoder/presenter prime state at the current
  // playhead. Useful for verifying that the deep-prime debounce is
  // actually filling the queue (queueLen should approach
  // window.__vybyDeepPrimeFrames within ~150ms+1.5s of settling).
  window.__vyby.primeStatus = () => {
    try {
      const editor = window.__vybyEditor
      if (!editor || !editor._playback || typeof editor._playback.describePrimeStatus !== 'function') {
        // eslint-disable-next-line no-console
        console.warn('[__vyby.primeStatus] editor not ready')
        return null
      }
      const status = editor._playback.describePrimeStatus()
      // eslint-disable-next-line no-console
      console.info('[__vyby.primeStatus]', status)
      return status
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[__vyby.primeStatus] failed', e)
      return null
    }
  }

  // Phase 11.1 diagnostic: snapshot of cross-clip audio handoff state.
  // Captures everything needed to debug "audio cuts off when crossing
  // into clip B" symptoms in a single console call. Pass explicit
  // `(uuidA, uuidB)` to compare two specific strips, or call with no
  // args to auto-detect: A = strip the playhead is currently inside on
  // an audio-bearing track, B = next strip on the same track.
  // Read-only — never mutates editor or audio pipeline state.
  window.__vyby.audioHandoffSnapshot = (uuidA = null, uuidB = null) => {
    try {
      const editor = window.__vybyEditor
      if (!editor) {
        // eslint-disable-next-line no-console
        console.warn('[__vyby.audioHandoffSnapshot] editor not ready')
        return null
      }
      const objects = editor._objects || {}
      const tracks = store.state.project?.tracks || {}
      const audioPipe = editor._audioPipe || null
      const audioStore = (typeof window !== 'undefined' && typeof window.vybyAudioStore === 'function')
        ? window.vybyAudioStore()
        : null
      const t = Number(editor._time)

      const stripMeta = (uuid) => {
        const obj = objects[uuid]
        if (!obj) return null
        const track = tracks[obj.track]
        const strip = track?.strips?.[obj.strip]
        if (!strip) return null
        return {
          uuid,
          obj,
          track,
          strip,
          start: Number(strip.start) || 0,
          length: Number(strip.length) || 0,
        }
      }

      // Auto-detect A/B when caller didn't pass them. Walk audio-bearing
      // tracks and find the first one with a strip the playhead is
      // inside; B is the closest next strip on that same track.
      if (!uuidA) {
        const byTrack = new Map()
        Object.keys(objects).forEach((uuid) => {
          const meta = stripMeta(uuid)
          if (!meta) return
          if (!meta.obj._mp4Player && !meta.obj.controller) return
          if (!byTrack.has(meta.strip.track)) byTrack.set(meta.strip.track, [])
          byTrack.get(meta.strip.track).push(meta)
        })
        byTrack.forEach((entries) => {
          if (uuidA) return
          entries.sort((a, b) => a.start - b.start)
          const current = entries.find((e) => t >= e.start - 0.5 && t <= e.start + e.length + 0.5)
          if (!current) return
          uuidA = current.uuid
          const next = entries.find((e) => e.uuid !== current.uuid && e.start >= current.start + current.length - 0.001)
          if (next) uuidB = next.uuid
        })
      }

      const describeStrip = (uuid, role) => {
        if (!uuid) return null
        const obj = objects[uuid]
        if (!obj) return { uuid, role, missing: true }
        const meta = stripMeta(uuid)
        const stored = audioPipe?._storedSources?.get?.(uuid) || null
        const decodedRegistered = !!editor._decodedSources?.has?.(uuid)
        const log = (typeof window !== 'undefined' && window.__vybyAudioPathLog?.[uuid]) || []
        const counts = {}
        log.forEach((e) => { counts[e.key] = (counts[e.key] || 0) + 1 })
        const tail = log.slice(-5).map((e) => ({ key: e.key, ts: e.ts, value: e.value }))
        const isReady = !!audioStore?.isReady?.(uuid)
        const storedInfo = audioStore?.debugInfo?.()?.sources?.find?.((s) => s.uuid === uuid) || null
        return {
          uuid,
          role,
          stripStart: meta?.start ?? null,
          stripLength: meta?.length ?? null,
          trackUuid: obj.track || null,
          assetType: (obj._mp4Player?._audioTrack ? 'video+audio' : null),
          decodedAudioEnabled: !!obj._decodedAudioEnabled,
          mp4PlayerStarted: !!obj._mp4Player?._started,
          mp4PlayerLoaded: !!obj._mp4Player?._info,
          audioCodecSupported: obj._mp4Player?._audioCodecSupported ?? null,
          hasStoredSource: !!stored,
          storedSourceLastRead: stored?.lastRead ?? null,
          storedSourceStripStart: stored?.stripStart ?? null,
          storedSourceStripLength: stored?.stripLength ?? null,
          storeIsReady: isReady,
          storeDurationSec: storedInfo?.durationSeconds ?? null,
          storeFrames: storedInfo?.frames ?? null,
          decodedSourceRegistered: decodedRegistered,
          prefillStatus: obj._prefillState?.status ?? null,
          prefillReason: obj._prefillState?.reason ?? null,
          controllerMuted: obj.controller?.muted ?? null,
          controllerPaused: obj.controller?.paused ?? null,
          controllerVolume: obj.controller?.volume ?? null,
          controllerCurrentTime: obj.controller?.currentTime ?? null,
          pathLogCounts: counts,
          pathLogTail: tail,
        }
      }

      const pipeDebug = audioPipe?.debugInfo?.() || null
      const out = {
        time: t,
        playing: !!editor._playing,
        clockRate: editor._clock?.getRate?.() ?? null,
        audioPipe: pipeDebug ? {
          playbackTime: pipeDebug.playbackTime ?? audioPipe?._playbackTime ?? null,
          playbackVelocity: audioPipe?._playbackVelocity ?? null,
          storedSourceCount: audioPipe?._storedSources?.size ?? null,
          queueSourceCount: audioPipe?.sources?.size ?? null,
          ringBufferedSec: pipeDebug.lastBufferedSeconds ?? null,
          underruns: pipeDebug.stats?.underruns ?? audioPipe?.stats?.underruns ?? null,
          framesWritten: audioPipe?.stats?.frames ?? null,
        } : null,
        strips: [describeStrip(uuidA, 'A'), describeStrip(uuidB, 'B')].filter(Boolean),
      }
      // eslint-disable-next-line no-console
      console.info('[__vyby.audioHandoffSnapshot]', out)
      return out
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[__vyby.audioHandoffSnapshot] failed', e)
      return null
    }
  }

  // Phase 6 debug: render the project's audio-only mix for the current
  // export In/Out range (or full timeline if none) and download it as a
  // WAV file. Useful for verifying the offline mixer matches preview audio
  // before we mux it into a final MP4. Lazy-loaded to avoid pulling the
  // mixer module into the main bundle if nobody calls it.
  window.__vyby.renderAudioOnly = async () => {
    try {
      const { offlineAudioMix, pcmFloat32ToWavBlob } =
        await import('@/editor/audio/offlineAudioMix')
      const tl = store.state.project?.timeline || {}
      const length = Number(tl.length) || 0
      const auto = !!tl.exportAuto
      const startSec = !auto && Number.isFinite(Number(tl.exportIn))
        ? Math.max(0, Math.min(Number(tl.exportIn), length))
        : 0
      const endSec = !auto && Number.isFinite(Number(tl.exportOut))
        ? Math.max(startSec, Math.min(Number(tl.exportOut), length))
        : length
      const editor = window.__vybyEditor || null
      if (!editor) {
        // eslint-disable-next-line no-console
        console.warn('[__vyby.renderAudioOnly] no editor on window. Call after the editor mounts.')
        return null
      }
      // eslint-disable-next-line no-console
      console.info('[__vyby.renderAudioOnly] mixing', { startSec, endSec })
      const mix = offlineAudioMix({ editor, startSec, endSec })
      if (!mix || !mix.frames) {
        // eslint-disable-next-line no-console
        console.warn('[__vyby.renderAudioOnly] no audio sources mixed (silent project or store not ready)')
        return null
      }
      const wav = pcmFloat32ToWavBlob(mix.samples, mix.sampleRate, mix.channels)
      const url = URL.createObjectURL(wav)
      const a = document.createElement('a')
      a.href = url
      a.download = 'audio.wav'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
      // eslint-disable-next-line no-console
      console.info('[__vyby.renderAudioOnly] downloaded', {
        frames: mix.frames,
        sampleRate: mix.sampleRate,
        channels: mix.channels,
        sources: mix.sourceCount,
        bytes: wav.size,
      })
      return mix
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[__vyby.renderAudioOnly] failed', e)
      return null
    }
  }
}

export default createStore({
  modules: {
    ui,
    ai,
    user,
    project,
    cmd,
    io
  },
  state: {
    initialized: false,
  },
  mutations: {
    initialize: (state, next) => {
      state.initialized = next
    }
  },
  plugins: [debugPlayPlugin, devGlobalsPlugin],
  actions: {
    init: async (store) => {
      if (store.state.initialized) return
      await store.dispatch('ui/init')
      await store.dispatch('user/init')
      store.commit('initialize', true)
    },
    login: async (store) => {
      await store.dispatch('io/init')
      setTimeout(() => {
        store.dispatch('io/open', {
          code: 'core',
          url: VUE_APP_AI_BACKEND,
          // path: '/vbapi/socket.io/',
          binds: {
            bctask: 'ai/bctask',
            'ai:status': 'ai/backStatus'
          }
        })
      }, 1000);
      /*
      */

      await store.dispatch('user/loadProfile')
      await store.dispatch('user/loadChats')

      await store.dispatch('project/init')
      await store.dispatch('ai/init')
    },
    logout: async (store) => {
      await store.dispatch('io/deinit')
      // await store.dispatch('project/init')
      // await store.dispatch('ai/init')
    }
  }
})
